import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle } from '../theme';
import { Plus, X, CreditCard, Check, Clock } from 'lucide-react';

// EMIPlansPage — Treatment EMI installment manager (Phase 2 / item #4).
// List + create + record-payment workflows. Each plan auto-builds its
// installment schedule on save; staff records each payment to keep the
// patient's outstanding balance accurate.
export default function EMIPlansPage({ API_BASE, patientsList = [] }) {
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    patientId: '', patientName: '', patientPhone: '',
    treatmentName: '', totalAmount: 0, downPayment: 0,
    installmentCount: 6, installmentAmount: 0, frequency: 'monthly',
    startDate: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_BASE}/emi`); setPlans(r.data || []); }
    catch { setPlans([]); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);
  // Auto-update installment amount as totals change.
  useEffect(() => {
    const remaining = Math.max(0, (Number(form.totalAmount) || 0) - (Number(form.downPayment) || 0));
    if (form.installmentCount > 0) {
      setForm(f => ({ ...f, installmentAmount: Math.round(remaining / f.installmentCount) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.totalAmount, form.downPayment, form.installmentCount]);

  const save = async () => {
    try {
      await axios.post(`${API_BASE}/emi`, form);
      setShowForm(false); load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const recordPayment = async (planId, serial) => {
    const amount = Number(window.prompt('Amount paid?'));
    if (!amount || amount <= 0) return;
    const mode = window.prompt('Mode (cash/card/upi/cheque)?', 'upi') || 'upi';
    try {
      await axios.post(`${API_BASE}/emi/${planId}/installments/${serial}/pay`, { amount, paymentMode: mode });
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ margin: 0, color: 'var(--color-text)' }}>💳 Treatment EMI Plans</h2>
        {!showForm && <button onClick={() => setShowForm(true)} style={{ ...btnStyle, marginTop: 0 }}><Plus size={14}/> New EMI Plan</button>}
      </div>

      {showForm && (
        <div style={{ ...cardStyle, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0, color: 'var(--color-text)' }}>+ New EMI Plan</h3>
            <button onClick={() => setShowForm(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}><X size={18}/></button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Patient *</label>
              <select style={inputStyle} value={form.patientId} onChange={e => {
                const p = patientsList.find(x => x._id === e.target.value);
                setForm({ ...form, patientId: e.target.value, patientName: p?.name || '', patientPhone: p?.phone || '' });
              }}>
                <option value="">Select patient</option>
                {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Treatment</label>
              <input style={inputStyle} value={form.treatmentName} onChange={e => setForm({ ...form, treatmentName: e.target.value })} placeholder="e.g. Implant + Crown"/>
            </div>
            <div><label style={labelStyle}>Total Amount (₹) *</label>
              <input type="number" style={inputStyle} value={form.totalAmount} onChange={e => setForm({ ...form, totalAmount: Number(e.target.value) })}/>
            </div>
            <div><label style={labelStyle}>Down Payment (₹)</label>
              <input type="number" style={inputStyle} value={form.downPayment} onChange={e => setForm({ ...form, downPayment: Number(e.target.value) })}/>
            </div>
            <div><label style={labelStyle}># Installments *</label>
              <input type="number" min={2} max={36} style={inputStyle} value={form.installmentCount} onChange={e => setForm({ ...form, installmentCount: Number(e.target.value) })}/>
            </div>
            <div><label style={labelStyle}>Per Installment (₹)</label>
              <input type="number" style={inputStyle} value={form.installmentAmount} onChange={e => setForm({ ...form, installmentAmount: Number(e.target.value) })}/>
            </div>
            <div><label style={labelStyle}>Frequency</label>
              <select style={inputStyle} value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}>
                {['monthly','fortnightly','weekly'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Start Date *</label>
              <input type="date" style={inputStyle} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}/>
            </div>
          </div>

          <button onClick={save} style={{ ...btnStyle, marginTop: 14 }}>Create Plan</button>
        </div>
      )}

      {/* Plans list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plans.length === 0 ? (
          <div style={{ ...cardStyle, padding: 30, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            No EMI plans yet. Click "New EMI Plan" to create one.
          </div>
        ) : plans.map(p => (
          <div key={p._id} style={{ ...cardStyle }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{p.patientName} — {p.treatmentName}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  ₹{p.totalAmount.toLocaleString('en-IN')} · {p.installmentCount} × {p.frequency}
                  · Status: <strong>{p.status}</strong>
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text)' }}>
                Paid: ₹{(p.installments || []).reduce((s, i) => s + (i.paidAmount || 0), 0).toLocaleString('en-IN')}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr>
                <th style={{ textAlign: 'left', padding: 6, color: 'var(--color-text-secondary)' }}>#</th>
                <th style={{ textAlign: 'left', padding: 6, color: 'var(--color-text-secondary)' }}>Due Date</th>
                <th style={{ textAlign: 'left', padding: 6, color: 'var(--color-text-secondary)' }}>Amount</th>
                <th style={{ textAlign: 'left', padding: 6, color: 'var(--color-text-secondary)' }}>Paid</th>
                <th style={{ textAlign: 'left', padding: 6, color: 'var(--color-text-secondary)' }}>Status</th>
                <th style={{ padding: 6 }}></th>
              </tr></thead>
              <tbody>
                {(p.installments || []).map(i => (
                  <tr key={i.serial} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 6, color: 'var(--color-text)' }}>{i.serial}</td>
                    <td style={{ padding: 6, color: 'var(--color-text)' }}>{new Date(i.dueDate).toLocaleDateString('en-IN')}</td>
                    <td style={{ padding: 6, color: 'var(--color-text)' }}>₹{i.amount.toLocaleString('en-IN')}</td>
                    <td style={{ padding: 6, color: 'var(--color-text)' }}>₹{(i.paidAmount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: 6 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        background: i.status === 'paid' ? '#d1fae5' : i.status === 'overdue' ? '#fee2e2' : i.status === 'partial' ? '#fef3c7' : '#e5e7eb',
                        color:      i.status === 'paid' ? '#065f46' : i.status === 'overdue' ? '#991b1b' : i.status === 'partial' ? '#92400e' : '#374151' }}>
                        {i.status}
                      </span>
                    </td>
                    <td style={{ padding: 6 }}>
                      {i.status !== 'paid' && (
                        <button onClick={() => recordPayment(p._id, i.serial)} style={{ padding: '4px 10px', fontSize: 11, background: theme.success, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                          Record Payment
                        </button>
                      )}
                      {i.status === 'paid' && <Check size={14} color={theme.success}/>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
